import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getRankerWeeklyCompare, getAskPriceChart } from '../../lib/api/ranker-api';
import RankerComplexSearchPopup from './RankerComplexSearchPopup';
import TopChart from './compare/TopChart';
import CountChart from './compare/CountChart';
import SplitTopChart from './compare/SplitTopChart';
import CompareSummaryTable from './compare/CompareSummaryTable';
import { MetricToggleGroup } from './compare/ChartCard';
import {
  PALETTE, METRIC_TOGGLES, SALE_MAIN_METRICS, LEASE_MAIN_METRICS, buildTopToggleGroups,
} from './compare/constants';
import {
  normalizeTarget, toDateKey, toTimestamp, toNumber, aggregateWeeklyRows,
} from './compare/utils';
import './RankingCompareModal.css';

const DEFAULT_METRIC_VISIBLE = {
  topPrice: true,
  // 호가↔실거래가는 라디오(둘 중 하나). 기본은 실거래가 ON.
  salePrice: false,
  saleRealPrice: true,
  leasePrice: false,
  leaseRealPrice: true,
  leasePriceRate: true,
  saleRecent8Weeks: false,
  leaseRecent8Weeks: false,
  saleCount: true,
  saleRealCount: false,
  leaseCount: true,
  leaseRealCount: false,
};

// 배타 토글(라디오): 둘 중 하나만 ON. 켤 때 짝을 끈다.
//  - 매물수 vs 실거래건수, 호가 vs 실거래가
const COUNT_EXCLUSIVE_PAIR = {
  saleCount: 'saleRealCount',
  saleRealCount: 'saleCount',
  leaseCount: 'leaseRealCount',
  leaseRealCount: 'leaseCount',
  salePrice: 'saleRealPrice',
  saleRealPrice: 'salePrice',
  leasePrice: 'leaseRealPrice',
  leaseRealPrice: 'leasePrice',
};

// 호가차트(getAskPriceChart) 포인트에서 최근8주(호가) 라인용 데이터 추출.
//  - dataSource='weekly_fix'(최근 호가) 포인트만 사용. 단, 해당 값이 없으면(응답 미제공) 전체 포인트로 폴백해 라인이 사라지지 않게 함.
const pickRecent8Points = (arr, priceKeys) => {
  const list = Array.isArray(arr) ? arr : [];
  const weeklyFix = list.filter((p) => String(p?.dataSource || '') === 'weekly_fix');
  const usingWeeklyFix = weeklyFix.length > 0;
  const mapped = (usingWeeklyFix ? weeklyFix : list)
    .map((p) => ({
      baseDate: p?.date ?? p?.baseDate,
      price: priceKeys.map((k) => p?.[k]).find((v) => v != null && v !== ''),
    }))
    .filter((p) => p.baseDate != null && p.price != null);
  // dataSource 미제공(폴백)이면 전체 호가차트가 잡혀 실거래 라인 전체가 대체되므로,
  // 최근 구간(마지막 시점 기준 약 7개월)만 최근8주로 간주한다.
  if (!usingWeeklyFix && mapped.length > 0) {
    const maxTs = mapped.reduce((m, p) => Math.max(m, toTimestamp(p.baseDate)), Number.NEGATIVE_INFINITY);
    if (Number.isFinite(maxTs)) {
      const cutoff = maxTs - 210 * 86400 * 1000; // ~7개월
      return mapped.filter((p) => toTimestamp(p.baseDate) >= cutoff);
    }
  }
  return mapped;
};

// 단지별 최신일 기준 rangeYears 범위로 주간 스냅샷을 자른다
const filterItemsByYears = (sourceItems, years) => {
  const latestTimestamp = sourceItems.reduce((latest, item) => {
    const itemLatest = item.weeklySnapshots.reduce((acc, row) => Math.max(acc, toTimestamp(row?.baseDate)), Number.NEGATIVE_INFINITY);
    return Math.max(latest, itemLatest);
  }, Number.NEGATIVE_INFINITY);

  if (!Number.isFinite(latestTimestamp)) return sourceItems;
  const cutoffTimestamp = latestTimestamp - (years > 0 ? years * 365 * 86400 * 1000 : 0);

  return sourceItems.map((item) => ({
    ...item,
    weeklySnapshots: item.weeklySnapshots.filter((row) => toTimestamp(row?.baseDate) >= cutoffTimestamp),
  }));
};

function RankingCompareModalV2({ targets = [], onClose, embedded = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [compareTargets, setCompareTargets] = useState([]);
  const [visibleByCode, setVisibleByCode] = useState({});
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [showCombinedTopChart, setShowCombinedTopChart] = useState(true);
  // 보이는 기간(년) — 데이터는 전체 로드하고 dataZoom 창만 이 값으로 (기본 3년, 이동은 휠클릭 드래그)
  const rangeYears = 3;
  const [syncedZoom, setSyncedZoom] = useState(null);
  const [brushSelection, setBrushSelection] = useState(null);
  const [brushPanelActive, setBrushPanelActive] = useState(true); // 구간비교표 활성화/비활성화
  const [metricVisible, setMetricVisible] = useState(DEFAULT_METRIC_VISIBLE);
  // 최근8주(호가차트) 지연 로딩 결과 — { compareKey: { sale: [...], lease: [...] } }
  const [recent8ByKey, setRecent8ByKey] = useState({});
  const recent8LoadedRef = useRef(new Set()); // '{compareKey}|sale' / '|lease' 중복요청 방지

  // 차트 인스턴스 refs — 동기화(hover/zoom/brush)에 사용
  const topSaleChartRef = useRef(null);
  const topLeaseChartRef = useRef(null);
  const splitTopChartRefs = useRef({});
  const saleCountChartRef = useRef(null);
  const leaseCountChartRef = useRef(null);
  const hoverSyncLockRef = useRef(false);
  const scrollBodyRef = useRef(null);
  const ctrlHeldRef = useRef(false); // Ctrl 눌림 상태 — 브러시(구간비교) 커서 on/off 판단용
  const zoomCommitTimerRef = useRef(null); // datazoom(pan/zoom) 상태 커밋 디바운스

  const getChartInstances = useCallback(() => {
    const splitTopCharts = Object.values(splitTopChartRefs.current || {})
      .map((entry) => entry?.getEchartsInstance?.());
    return [
      topSaleChartRef.current?.getEchartsInstance?.(),
      topLeaseChartRef.current?.getEchartsInstance?.(),
      ...splitTopCharts,
      saleCountChartRef.current?.getEchartsInstance?.(),
      leaseCountChartRef.current?.getEchartsInstance?.(),
    ].filter((chart) => chart && typeof chart.dispatchAction === 'function' && !chart.isDisposed?.());
  }, []);

  const registerSplitRef = useCallback((key, instance) => {
    if (instance) splitTopChartRefs.current[key] = instance;
    else delete splitTopChartRefs.current[key];
  }, []);

  // Ctrl 키 상태에 따라 브러시(구간선택) 커서를 켜고 끔.
  //  - active=true  → 브러시 활성 → 왼쪽 드래그 = 구간비교 선택
  //  - active=false → 브러시 비활성 → 왼쪽 드래그 = 기간 이동(dataZoom inside pan)
  const applyBrushCursor = useCallback((active) => {
    getChartInstances().forEach((chart) => {
      try {
        if (active) {
          chart.dispatchAction({ type: 'takeGlobalCursor', key: 'brush', brushOption: { brushType: 'lineX', brushMode: 'single' } });
        } else {
          // key 없이 dispatch → 전역 커서 해제(브러시 OFF) → 왼쪽 드래그가 dataZoom pan 으로 동작
          chart.dispatchAction({ type: 'takeGlobalCursor' });
        }
      } catch (e) { /* stale chart */ }
    });
  }, [getChartInstances]);

  const onToggleMetric = useCallback((key, checked) => {
    setMetricVisible((prev) => {
      const next = { ...prev, [key]: checked };
      const sibling = COUNT_EXCLUSIVE_PAIR[key];
      // 수량 차트는 라디오: 하나를 켜면 짝을 끈다
      if (sibling && checked) next[sibling] = false;
      // 최근8주는 '실거래가' 선택 시에만 유효 → 호가 선택(실거래가 OFF)되면 강제 해제
      if (!next.saleRealPrice) next.saleRecent8Weeks = false;
      if (!next.leaseRealPrice) next.leaseRecent8Weeks = false;
      return next;
    });
  }, []);
  const onBrushClose = useCallback(() => setBrushSelection(null), []);

  // ESC 로 구간비교표(브러시 선택) 닫기. (모달 자체는 ESC 로 닫지 않음)
  useEffect(() => {
    if (!brushSelection) return undefined;
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setBrushSelection(null);
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [brushSelection]);

  // 표시 여부 파생값
  const showSaleTopChart = metricVisible.topPrice || SALE_MAIN_METRICS.some((key) => metricVisible[key]);
  const showLeaseTopChart = metricVisible.topPrice || LEASE_MAIN_METRICS.some((key) => metricVisible[key]);
  const showTopChart = showSaleTopChart || showLeaseTopChart;
  const showSaleCountChart = metricVisible.saleCount || metricVisible.saleRealCount;
  const showLeaseCountChart = metricVisible.leaseCount || metricVisible.leaseRealCount;

  // brush 영역 좌표를 "드래그한 그 차트의 실제 x축 카테고리(dates)" 기준으로 날짜로 변환.
  // category 축의 coordRange 는 인덱스(소수 가능)이므로, 전역 배열이 아니라 소스 차트의 축 데이터로 매핑해야 정확하다.
  // (차트마다 x축 dates 가 다름: TopChart 는 topPriceDate/recent8weeks 포함, CountChart 는 weekly 만)
  const resolveBrushAreaToDates = useCallback((chart, area) => {
    const coordRange = area?.coordRange;
    if (!Array.isArray(coordRange) || coordRange.length < 1) return null;
    let catData = [];
    try {
      const opt = chart?.getOption?.();
      const xAxis = Array.isArray(opt?.xAxis) ? opt.xAxis[0] : opt?.xAxis;
      catData = Array.isArray(xAxis?.data) ? xAxis.data : [];
    } catch (e) { catData = []; }

    const coordToDate = (coord) => {
      const raw = coord;
      // 날짜 문자열(비-순수숫자)이면 그대로 사용
      if (typeof raw === 'string' && !/^-?\d+(\.\d+)?$/.test(raw.trim())) {
        return toDateKey(raw) || '';
      }
      // 그 외에는 소스 차트 카테고리 인덱스로 해석
      const numeric = Number(raw);
      if (!Number.isFinite(numeric) || catData.length < 1) return '';
      const index = Math.max(0, Math.min(catData.length - 1, Math.round(numeric)));
      const cat = catData[index];
      const val = (cat && typeof cat === 'object') ? cat.value : cat;
      return toDateKey(val) || '';
    };

    const startDate = coordToDate(coordRange[0]);
    const endDate = coordToDate(coordRange[1] ?? coordRange[0]);
    if (!startDate || !endDate) return null;
    const sorted = [startDate, endDate].sort((a, b) => toTimestamp(a) - toTimestamp(b));
    return { startDate: sorted[0], endDate: sorted[1] };
  }, []);

  // 차트 이벤트 (zoom 동기화 / brush 선택). echarts-for-react 는 핸들러 2번째 인자로 소스 차트 인스턴스를 넘겨준다.
  const chartEvents = useMemo(() => ({
    // 네이티브 pan(드래그)/줌(Ctrl+휠)으로 발생. 퍼센트(start/end)만 사용.
    datazoom: (params, sourceChart) => {
      const raw = Array.isArray(params?.batch) && params.batch.length > 0 ? params.batch[0] : params;
      if (!raw || typeof raw !== 'object') return;
      const start = raw.start;
      const end = raw.end;
      if (start === undefined || end === undefined) return;
      zoomWindowRef.current = { start, end };
      // 다른 차트들은 재렌더 없이 즉시 동기화(setOption merge) → 드래그 끊김/스냅백 없음.
      getChartInstances().forEach((c) => {
        if (c === sourceChart) return;
        try { c.setOption({ dataZoom: [{ start, end }] }); } catch (e) { /* stale chart */ }
      });
      // React state 는 디바운스로 1회 커밋(드래그/휠 종료 후) → 과도 재렌더/드래그 중 스냅백 방지.
      if (zoomCommitTimerRef.current) clearTimeout(zoomCommitTimerRef.current);
      zoomCommitTimerRef.current = setTimeout(() => {
        setSyncedZoom((prev) => (prev && prev.start === start && prev.end === end ? prev : { start, end }));
      }, 180);
    },
    brushEnd: (params, chart) => {
      const areas = params?.areas;
      if (!areas || areas.length === 0) { setBrushSelection(null); return; }
      const resolved = resolveBrushAreaToDates(chart, areas[0]);
      if (!resolved) return; // 좌표 해석 실패 시 기존 선택 유지(드래그가 무효화되는 것 방지)
      setBrushSelection((prev) => (
        prev?.startDate === resolved.startDate && prev?.endDate === resolved.endDate ? prev : resolved
      ));
    },
  }), [resolveBrushAreaToDates, getChartInstances]);

  // targets -> compareTargets 정규화
  useEffect(() => {
    const normalized = (Array.isArray(targets) ? targets : []).map((target) => normalizeTarget(target)).filter(Boolean);
    setCompareTargets(normalized);
  }, [targets]);

  // 모달 모드: body 스크롤 잠금 (ESC 로는 닫히지 않음)
  useEffect(() => {
    if (embedded) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [embedded]);

  // 비교 데이터 조회
  useEffect(() => {
    // (단지코드, ptpNo) 페어 단위로 조회 — weekly 는 단지코드+ptp_no_csv 로 조회되어야 함
    const pairs = (Array.isArray(compareTargets) ? compareTargets : [])
      .map((target) => ({ code: String(target?.code || '').trim(), ptpNo: String(target?.ptpNo || '').trim() }))
      .filter((p) => p.code);

    if (pairs.length < 1) { setItems([]); return undefined; }

    const keyOf = (code, ptpNo) => `${code}|${ptpNo || ''}`;

    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await getRankerWeeklyCompare(pairs);
        if (!alive) return;
        const rawItems = Array.isArray(response?.items) ? response.items : [];
        const byKey = new Map(rawItems.map((item) => [keyOf(String(item?.naverDanjiCode || ''), String(item?.ptpNo || '')), item]));
        const byCode = new Map(rawItems.map((item) => [String(item?.naverDanjiCode || ''), item]));
        // 같은 단지코드가 평형만 다르게 여러 번 들어오는지 카운트 (표시명 구분용)
        const codeCounts = pairs.reduce((acc, p) => { acc[p.code] = (acc[p.code] || 0) + 1; return acc; }, {});

        const ordered = pairs.map(({ code, ptpNo }) => {
          const source = byKey.get(keyOf(code, ptpNo)) || byCode.get(code) || {};
          const target = compareTargets.find((row) => String(row?.code || '') === code && String(row?.ptpNo || '') === ptpNo);
          const address = source?.addressRoad || source?.roadAddress || source?.address || source?.jibunAddress
            || target?.addressRoad || target?.roadAddress || target?.address || '';
          const familyCnt = toNumber(
            source?.familyCnt ?? source?.householdCount ?? source?.households
            ?? target?.familyCnt ?? target?.householdCount ?? target?.households,
          );
          const useApproveDate = source?.useApproveDate || source?.useApprovalDate || target?.useApproveDate || target?.useApprovalDate || '';
          const buildingName = source?.buildingName || target?.name || code;
          const planTypeName = String(target?.planTypeName || source?.planTypeName || '').trim();
          // 같은 단지를 평형만 다르게 비교하는 경우 "단지(~평)" 으로 구분 표기 (타입 대신 평, 상세모달과 동일)
          const planTypeName2 = String(target?.planTypeName2 || source?.planTypeName2 || '').trim();
          // 타입/면적 문자열에서 평 라벨 추출 (숫자 ≤40 = 평, 그 외 = ㎡ → 평 환산)
          const parsePyeongLabel = (str) => {
            const nums = (String(str || '').match(/\d+(?:\.\d+)?/g) || [])
              .map(Number).filter((n) => Number.isFinite(n) && n > 0)
              .map((n) => (n <= 40 ? n * 3.305785 : n)); // ㎡ 기준으로 통일
            if (nums.length === 0) return '';
            const ps = nums.map((m2) => Math.round(m2 / 3.305785));
            const pMin = Math.min(...ps);
            const pMax = Math.max(...ps);
            return pMin === pMax ? `${pMin}평` : `${pMin}~${pMax}평`;
          };
          // 1순위: 면적→평 라벨(pyeongLabel), 2순위: planTypeName2, 3순위: planTypeName(타입 문자열) 파싱
          const areaLabel = String(target?.pyeongLabel || '').trim()
            || parsePyeongLabel(planTypeName2)
            || parsePyeongLabel(planTypeName);
          const displayName = (codeCounts[code] > 1 && areaLabel) ? `${buildingName}(${areaLabel})` : buildingName;
          return {
            compareKey: keyOf(code, ptpNo),
            naverDanjiCode: code,
            ptpNo,
            buildingName,
            planTypeName,
            displayName,
            address,
            familyCnt,
            useApproveDate,
            // 단지 비교 표용 메타
            regionName: source?.regionName || address || '',
            sigunguGrade: source?.sigunguGrade ?? null,
            dongGrade: source?.dongGrade ?? null,
            pyeongLabel: areaLabel || '',
            topPrice: toNumber(source?.topPrice),
            topPriceDate: toDateKey(source?.topPriceDate || source?.top_price_date),
            topPriceDealType: toNumber(source?.topPriceDealType || source?.top_price_deal_type),
            // 단지비교 표: 적현율(%) / 적고가(만원). compare API(source) 우선, 없으면 랭킹 행(target)
            jeokHyunRate: toNumber(source?.jeokHyunRate ?? source?.jeok_hyun_rate ?? target?.jeokHyunRate),
            properHighPrice: toNumber(source?.properHighPrice ?? source?.proper_high_price ?? target?.properHighPrice),
            weeklySnapshots: aggregateWeeklyRows(Array.isArray(source?.weeklySnapshots) ? source.weeklySnapshots : []),
            // 최근8주는 토글 ON 시 별도 API(getAskPriceChart)로 지연 로딩 → 여기선 빈 배열
            saleRecent8Weeks: [],
            leaseRecent8Weeks: [],
          };
        });
        setItems(ordered);
        // 새 비교 데이터 로드 시 최근8주 캐시 초기화(토글 ON이면 아래 effect가 다시 로딩)
        setRecent8ByKey({});
        recent8LoadedRef.current = new Set();
      } catch (e) {
        if (!alive) return;
        setItems([]);
        setError(e?.message || '비교 데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [compareTargets]);

  // items 변경 시 표시 상태 동기화 (식별자: compareKey = 단지코드|ptpNo)
  useEffect(() => {
    if (items.length === 0) { setVisibleByCode({}); return; }
    setVisibleByCode((prev) => {
      const next = {};
      items.forEach((item) => { next[item.compareKey] = prev[item.compareKey] !== false; });
      return next;
    });
  }, [items]);

  const colorByCode = useMemo(() => {
    const map = new Map();
    items.forEach((item, index) => map.set(item.compareKey, PALETTE[index % PALETTE.length]));
    return map;
  }, [items]);

  const visibleItems = useMemo(
    () => items
      .filter((item) => visibleByCode[item.compareKey] !== false)
      .map((item) => {
        const r8 = recent8ByKey[item.compareKey];
        return r8
          ? { ...item, saleRecent8Weeks: r8.sale || [], leaseRecent8Weeks: r8.lease || [] }
          : item;
      }),
    [items, visibleByCode, recent8ByKey],
  );

  // 최근8주 토글 ON 시 별도 API(getAskPriceChart)로 지연 로딩 (매매 dealType=1 / 전세 dealType=2)
  useEffect(() => {
    const wantSale = metricVisible.saleRecent8Weeks;
    const wantLease = metricVisible.leaseRecent8Weeks;
    if (!wantSale && !wantLease) return undefined;
    const targetsToLoad = items.filter((item) => visibleByCode[item.compareKey] !== false);
    if (targetsToLoad.length === 0) return undefined;

    let alive = true;
    (async () => {
      for (const item of targetsToLoad) {
        const key = item.compareKey;
        const saleTag = `${key}|sale`;
        if (wantSale && !recent8LoadedRef.current.has(saleTag)) {
          recent8LoadedRef.current.add(saleTag);
          try {
            const resp = await getAskPriceChart(item.naverDanjiCode, { ptpNo: item.ptpNo, dealType: 1 });
            const pts = pickRecent8Points(resp?.items, ['salePrice', 'saleMaxPrice', 'price']);
            if (!alive) return;
            setRecent8ByKey((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), sale: pts } }));
          } catch (e) {
            if (alive) setRecent8ByKey((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), sale: [] } }));
          }
        }
        const leaseTag = `${key}|lease`;
        if (wantLease && !recent8LoadedRef.current.has(leaseTag)) {
          recent8LoadedRef.current.add(leaseTag);
          try {
            const resp = await getAskPriceChart(item.naverDanjiCode, { ptpNo: item.ptpNo, dealType: 2 });
            const pts = pickRecent8Points(resp?.items, ['leasePrice', 'leaseMaxPrice', 'price']);
            if (!alive) return;
            setRecent8ByKey((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), lease: pts } }));
          } catch (e) {
            if (alive) setRecent8ByKey((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), lease: [] } }));
          }
        }
      }
    })();
    return () => { alive = false; };
  }, [metricVisible.saleRecent8Weeks, metricVisible.leaseRecent8Weeks, items, visibleByCode]);

  const initialKeySet = useMemo(() => {
    const set = new Set();
    (Array.isArray(targets) ? targets : []).forEach((target) => {
      const normalized = normalizeTarget(target);
      if (normalized?.code) set.add(`${normalized.code}|${normalized.ptpNo || ''}`);
    });
    return set;
  }, [targets]);

  // 차트에는 전체 년도 데이터를 모두 로드(연도 슬라이스 제거). 보이는 구간은 dataZoom 창으로 제어.
  const filteredItems = visibleItems;

  // 전체 데이터의 날짜 범위(min/max) — 로드된 items 기준(가시성 토글에 영향받지 않게)
  const dataDateRange = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    items.forEach((item) => (item.weeklySnapshots || []).forEach((row) => {
      const t = toTimestamp(row?.baseDate);
      if (Number.isFinite(t)) { if (t < min) min = t; if (t > max) max = t; }
    }));
    return (Number.isFinite(min) && Number.isFinite(max) && max > min) ? { min, max } : null;
  }, [items]);

  // "최근 N년" 창의 시작 퍼센트 계산 (end 는 항상 100)
  const zoomStartForYears = useCallback((years) => {
    if (!dataDateRange) return 0;
    const totalMs = dataDateRange.max - dataDateRange.min;
    if (totalMs <= 0) return 0;
    const windowMs = Math.max(1, years) * 365 * 86400 * 1000;
    return Math.max(0, Math.min(99, 100 * (1 - Math.min(windowMs, totalMs) / totalMs)));
  }, [dataDateRange]);

  // 데이터의 날짜 범위가 "실제로 바뀔 때만"(새 데이터 로드) dataZoom 창을 "최근 N년"으로 초기화.
  // 매 렌더/팬마다 재설정되면 팬 직후 창이 3년으로 튕겨 "한 번 이동 후 먹통"이 되므로, 범위 키로 1회만.
  const lastZoomRangeKeyRef = useRef(null);
  useEffect(() => {
    if (!dataDateRange) return;
    const rangeKey = `${dataDateRange.min}|${dataDateRange.max}|${rangeYears}`;
    if (lastZoomRangeKeyRef.current === rangeKey) return;
    lastZoomRangeKeyRef.current = rangeKey;
    setSyncedZoom({ start: zoomStartForYears(rangeYears), end: 100 });
  }, [rangeYears, dataDateRange, zoomStartForYears]);

  // 현재 보이는 창(%) 추적 — 휠클릭 팬 시작 기준값으로 사용
  const zoomWindowRef = useRef({ start: 0, end: 100 });
  useEffect(() => {
    if (syncedZoom && syncedZoom.start != null && syncedZoom.end != null) {
      zoomWindowRef.current = { start: syncedZoom.start, end: syncedZoom.end };
    }
  }, [syncedZoom]);
  useEffect(() => () => { if (zoomCommitTimerRef.current) clearTimeout(zoomCommitTimerRef.current); }, []);

  // 구간비교 패널용 시작/종료 row 추출
  const brushRangeRows = useMemo(() => {
    if (!brushSelection?.startDate || !brushSelection?.endDate) return null;
    const { startDate, endDate } = brushSelection;
    const startTime = toTimestamp(startDate);
    const endTime = toTimestamp(endDate);
    const results = [];

    filteredItems.forEach((item) => {
      const rows = Array.isArray(item?.weeklySnapshots) ? item.weeklySnapshots : [];
      const rowMap = new Map(rows.map((row) => [row.baseDate, row]));
      const startRow = rowMap.get(startDate) || rows.find((row) => toTimestamp(row?.baseDate) >= startTime) || rows[0];
      const endRow = rowMap.get(endDate) || [...rows].reverse().find((row) => toTimestamp(row?.baseDate) <= endTime) || rows[rows.length - 1];
      const currentRow = rows[rows.length - 1] || endRow; // 현재 = 가장 최근 스냅샷
      if (!startRow && !endRow) return;
      // 구간(시작~종료) 내 각 지표 평균
      const rangeRows = rows.filter((row) => {
        const t = toTimestamp(row?.baseDate);
        return Number.isFinite(t) && t >= startTime && t <= endTime;
      });
      const avgRow = {};
      if (rangeRows.length > 0) {
        const sums = {};
        const counts = {};
        rangeRows.forEach((row) => {
          Object.keys(row || {}).forEach((k) => {
            const v = Number(row[k]);
            if (Number.isFinite(v)) { sums[k] = (sums[k] || 0) + v; counts[k] = (counts[k] || 0) + 1; }
          });
        });
        Object.keys(sums).forEach((k) => { avgRow[k] = counts[k] ? sums[k] / counts[k] : null; });
      }
      results.push({
        name: item.displayName || item.buildingName || item.naverDanjiCode,
        color: colorByCode.get(item.compareKey) || '#334155',
        startRow,
        endRow,
        currentRow,
        avgRow,
      });
    });

    return { startDate, endDate, items: results };
  }, [brushSelection, filteredItems, colorByCode]);

  // 토글이 꺼져 있으면 차트 오버레이/우측 패널에 구간비교표를 렌더하지 않음(브러시 선택 자체는 유지)
  const effectiveBrushRangeRows = brushPanelActive ? brushRangeRows : null;
  const hasBrushRange = !!(brushRangeRows && Array.isArray(brushRangeRows.items) && brushRangeRows.items.length > 0);

  const hasAnyMetricVisible = METRIC_TOGGLES.some((metric) => metricVisible[metric.key] !== false);
  const useSplitTopChart = visibleItems.length > 1 && !showCombinedTopChart;
  const topChartCount = Number(showSaleTopChart) + Number(showLeaseTopChart);
  const mainTopChartHeight = (showSaleCountChart || showLeaseCountChart)
    ? (topChartCount > 1 ? 500 : 680)
    : (topChartCount > 1 ? 640 : 1080);
  const countChartHeight = 130;

  // 옵션 재생성(메트릭 토글/줌/기간 변경, notMerge) 후에도 brush 를 자동 on 유지
  // (echarts.connect 는 수동 hover/zoom/brush 동기화와 중복 + 이벤트 핑퐁으로 스택오버플로를
  //  유발할 수 있어 사용하지 않는다. 동기화는 아래 수동 effect 들이 전담)
  useEffect(() => {
    // brushSelection/차트구성 변경으로 setOption(notMerge) 이 재실행되면 전역 브러시 커서가 리셋되므로,
    // 그때마다 현재 Ctrl 눌림 상태에 맞춰 브러시 커서를 복원한다(기본: 꺼짐 → 드래그=기간 이동).
    applyBrushCursor(ctrlHeldRef.current);
  }, [applyBrushCursor, metricVisible, filteredItems, syncedZoom, rangeYears, useSplitTopChart, brushSelection, showSaleTopChart, showLeaseTopChart, showSaleCountChart, showLeaseCountChart]);

  // Ctrl 키를 누르는 동안에만 브러시(구간비교) 활성화. 떼면 비활성 → 드래그가 기간 이동으로 동작.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.key === 'Control' || e.key === 'Meta' || e.ctrlKey || e.metaKey) && !ctrlHeldRef.current) {
        ctrlHeldRef.current = true;
        applyBrushCursor(true);
      }
    };
    const onKeyUp = (e) => {
      if ((e.key === 'Control' || e.key === 'Meta' || (!e.ctrlKey && !e.metaKey)) && ctrlHeldRef.current) {
        ctrlHeldRef.current = false;
        applyBrushCursor(false);
      }
    };
    // 창 포커스 이탈/블러 시 Ctrl 눌림 상태 해제(브러시가 계속 켜져있는 것 방지)
    const onBlur = () => {
      if (ctrlHeldRef.current) {
        ctrlHeldRef.current = false;
        applyBrushCursor(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [applyBrushCursor]);

  // 차트 위에서 일반(Ctrl 없는) 휠은 페이지가 스크롤되도록 처리.
  // 스크롤 컨테이너에 capture 단계로 리스너를 달아 ECharts(자식)보다 먼저 가로채,
  // Ctrl 없는 휠은 직접 스크롤 + preventDefault/stopPropagation(ECharts 로 전달 차단),
  // Ctrl 휠은 그대로 통과시켜 ECharts 줌이 처리하게 한다.
  useEffect(() => {
    const scroller = scrollBodyRef.current;
    if (!scroller) return undefined;
    const onWheelCapture = (e) => {
      if (e.ctrlKey || e.metaKey) return; // 줌: ECharts 로 통과
      // 차트 영역 위에서만 가로챔(그 외 영역은 브라우저 기본 스크롤 사용)
      const overChart = e.target?.closest?.('.ranking-compare-chart-wrap');
      if (!overChart) return;
      let delta = e.deltaY;
      if (e.deltaMode === 1) delta *= 16;                 // line -> px
      else if (e.deltaMode === 2) delta *= scroller.clientHeight; // page -> px
      scroller.scrollTop += delta;
      e.preventDefault();
      e.stopPropagation();
    };
    scroller.addEventListener('wheel', onWheelCapture, { capture: true, passive: false });
    return () => scroller.removeEventListener('wheel', onWheelCapture, { capture: true });
  }, []);

  // brushSelection -> 모든 차트에 brush 영역 반영
  useEffect(() => {
    const charts = getChartInstances();
    if (charts.length === 0) return;
    const safeBrush = (chart, areas) => {
      try { chart.dispatchAction({ type: 'brush', areas }); } catch (e) { /* stale chart */ }
    };
    if (!brushSelection?.startDate || !brushSelection?.endDate) {
      charts.forEach((chart) => safeBrush(chart, []));
      return;
    }
    const area = { brushType: 'lineX', xAxisIndex: 0, coordRange: [brushSelection.startDate, brushSelection.endDate] };
    charts.forEach((chart) => safeBrush(chart, [area]));
  }, [brushSelection, getChartInstances, showSaleCountChart, showLeaseCountChart, showSaleTopChart, showLeaseTopChart, useSplitTopChart]);

  // 차트 간 hover(axisPointer) 동기화
  useEffect(() => {
    const charts = getChartInstances();
    if (charts.length === 0) return undefined;

    // 차트마다 series/dataIndex 구성이 달라 seriesIndex 기반 showTip 은 getDataParams 크래시를
    // 유발할 수 있으므로, 축값(xAxisValue) 기반으로만 툴팁을 동기화한다.
    const safeDispatch = (target, action) => {
      try {
        target.dispatchAction(action);
      } catch (e) {
        /* 업데이트 중 일시적으로 stale 한 차트 — 무시 */
      }
    };
    const syncHover = (sourceChart, axisValueRaw) => {
      if (hoverSyncLockRef.current) return;
      if (axisValueRaw === undefined || axisValueRaw === null || axisValueRaw === '') return;
      const axisValue = toDateKey(axisValueRaw) || axisValueRaw;
      hoverSyncLockRef.current = true;
      try {
        charts.forEach((target) => {
          if (!target || target === sourceChart) return;
          safeDispatch(target, { type: 'updateAxisPointer', xAxisIndex: 0, value: axisValue });
          safeDispatch(target, { type: 'showTip', xAxisIndex: 0, xAxisValue: axisValue });
        });
      } finally {
        hoverSyncLockRef.current = false;
      }
    };
    const clearHover = (sourceChart) => {
      if (hoverSyncLockRef.current) return;
      hoverSyncLockRef.current = true;
      try {
        charts.forEach((target) => { if (target && target !== sourceChart) safeDispatch(target, { type: 'hideTip' }); });
      } finally {
        hoverSyncLockRef.current = false;
      }
    };

    const listeners = charts.map((chart) => {
      const onUpdateAxisPointer = (params) => {
        const axisInfo = Array.isArray(params?.axesInfo) ? params.axesInfo[0] : null;
        if (axisInfo) syncHover(chart, axisInfo.value);
      };
      const onGlobalOut = () => clearHover(chart);
      chart.on('updateAxisPointer', onUpdateAxisPointer);
      chart.on('globalout', onGlobalOut);
      return { chart, onUpdateAxisPointer, onGlobalOut };
    });

    return () => {
      listeners.forEach(({ chart, onUpdateAxisPointer, onGlobalOut }) => {
        chart.off('updateAxisPointer', onUpdateAxisPointer);
        chart.off('globalout', onGlobalOut);
      });
    };
  }, [getChartInstances, showSaleCountChart, showLeaseCountChart, showSaleTopChart, showLeaseTopChart, showTopChart, useSplitTopChart, filteredItems.length]);

  const handleAddCompareTarget = (target) => {
    const normalized = normalizeTarget(target);
    if (!normalized?.code) return;
    // 동일 단지라도 평형(ptpNo)이 다르면 별개 대상 (compareKey = code|ptpNo)
    const key = `${normalized.code}|${normalized.ptpNo || ''}`;
    setCompareTargets((prev) => (
      prev.some((row) => `${String(row?.code || '')}|${String(row?.ptpNo || '')}` === key) ? prev : [...prev, normalized]
    ));
    setVisibleByCode((prev) => ({ ...prev, [key]: true }));
  };

  const handleRemoveCompareTarget = (code, ptpNo = '') => {
    if (!code) return;
    const removeKey = `${code}|${ptpNo || ''}`;
    const item = items.find((row) => row.compareKey === removeKey);
    const label = item?.displayName || item?.buildingName || code;
    if (!window.confirm(`${label} 단지를 비교대상에서 제거하시겠습니까?`)) return;
    setCompareTargets((prev) => prev.filter((row) => `${String(row?.code || '')}|${String(row?.ptpNo || '')}` !== removeKey));
    setVisibleByCode((prev) => {
      const next = { ...prev };
      delete next[removeKey];
      return next;
    });
  };

  // 상단 가격차트(매매가/전세가 실거래가)가 토글 전부 꺼져 사라질 때, 그 자리에 토글바 + 안내 표시
  const renderTopPlaceholder = (variant) => (
    <div className="ranking-compare-empty-toggles">
      <div className="ranking-compare-state" style={{ marginBottom: 8 }}>
        {variant === 'sale' ? '매매호가 · 실거래가 차트' : '전세호가 · 실거래가 차트'} — 옵션을 활성화해주세요
      </div>
      <MetricToggleGroup groups={buildTopToggleGroups(variant, metricVisible, visibleItems.length > 1 && showCombinedTopChart)} metricVisible={metricVisible} onToggle={onToggleMetric} groupName={`placeholder-${variant}`} />
    </div>
  );

  // 차트 컴포넌트에 공통 전달되는 props
  const commonChartProps = {
    filteredItems,
    colorByCode,
    metricVisible,
    syncedZoom,
    brushRangeRows: effectiveBrushRangeRows,
    onToggle: onToggleMetric,
    onBrushClose,
    chartEvents,
  };

  return (
    <div className={`ranking-compare-overlay ${embedded ? 'embedded' : ''}`.trim()} onClick={embedded ? undefined : onClose}>
      <div className={`ranking-compare-modal ${embedded ? 'embedded' : ''}`.trim()} onClick={embedded ? undefined : (event) => event.stopPropagation()}>
        <header className="ranking-compare-header">
          <h3>단지 비교</h3>
          <div className="ranking-compare-header-actions">
            {typeof onClose === 'function' && (
              <button type="button" className="ranking-compare-close" onClick={onClose} aria-label="닫기">×</button>
            )}
          </div>
        </header>

        <div className="ranking-compare-scroll-body" ref={scrollBodyRef}>
          {loading && <div className="ranking-compare-state">비교 데이터를 불러오는 중입니다...</div>}
          {!loading && error && <div className="ranking-compare-state error">{error}</div>}
          {!loading && !error && items.length === 0 && <div className="ranking-compare-state">비교할 데이터가 없습니다.</div>}
          {!loading && !error && items.length > 0 && visibleItems.length === 0 && (
            <div className="ranking-compare-state">표시 중인 단지가 없습니다. 상단 단지 칩에서 ON으로 전환해 주세요.</div>
          )}
          {!loading && !error && items.length > 0 && visibleItems.length > 0 && !hasAnyMetricVisible && (
            <div className="ranking-compare-empty-toggles">
              <div className="ranking-compare-state" style={{ marginBottom: 8 }}>표시할 항목이 없습니다. 아래 항목을 하나 이상 켜주세요.</div>
              <MetricToggleGroup
                toggleKeys={METRIC_TOGGLES.map((metric) => metric.key)}
                metricVisible={metricVisible}
                onToggle={onToggleMetric}
              />
            </div>
          )}

          {!loading && !error && items.length > 0 && visibleItems.length > 0 && hasAnyMetricVisible && (
            <div className="ranking-compare-content">
              <CompareSummaryTable items={visibleItems} colorByCode={colorByCode} />
              <div className="ranking-compare-complex-bar">
                <div className="complex-bar-left">
                {items.length > 1 && (
                  <label className="complex-toggle-merge-option" style={{ marginRight: 8 }}>
                    <input
                      type="checkbox"
                      checked={!showCombinedTopChart}
                      onChange={(event) => setShowCombinedTopChart(!event.target.checked)}
                    />
                    <span>단지별 보기</span>
                  </label>
                )}
                {items.map((item) => {
                  const key = item.compareKey;
                  const checked = visibleByCode[key] !== false;
                  const color = colorByCode.get(key) || '#334155';
                  const isAddedByPopup = !initialKeySet.has(key);
                  const toggleVisible = () => setVisibleByCode((prev) => ({ ...prev, [key]: !checked }));
                  return (
                    <div key={key} className={`compare-complex-tag ${checked ? 'on' : 'off'}`}>
                      <button type="button" className="compare-complex-toggle" onClick={toggleVisible} title={checked ? '숨기기' : '표시'}>
                        <span className="tag-dot" style={{ backgroundColor: color }} />
                        <span className="tag-name">{item.displayName}</span>
                      </button>
                      <button type="button" className="compare-complex-x" onClick={toggleVisible} aria-label={checked ? '숨기기' : '표시'} title={checked ? '숨기기' : '표시'}>×</button>
                      {isAddedByPopup && (
                        <button type="button" className="compare-complex-del" onClick={() => handleRemoveCompareTarget(item.naverDanjiCode, item.ptpNo)} aria-label="비교에서 제거" title="비교에서 제거">✕</button>
                      )}
                    </div>
                  );
                })}
                </div>
                <div className="complex-bar-right">
                  {hasBrushRange && (
                    <label className="complex-toggle-merge-option">
                      <input type="checkbox" checked={brushPanelActive} onChange={(event) => setBrushPanelActive(event.target.checked)} />
                      <span>구간비교표 표시</span>
                    </label>
                  )}
                  <button type="button" className="complex-add-compare-btn" onClick={() => setSearchPopupOpen(true)}>
                    + 비교 단지 추가하기
                  </button>
                </div>
              </div>
              <div className="ranking-compare-content-row">
              <div className="ranking-compare-chart-wrap">
                <div className="ranking-compare-pan-hint" aria-hidden="true">
                  🖱️ 휠: 상·하 스크롤 · Ctrl+휠: 확대/축소(포인터 중심) · 드래그: 기간 좌·우 이동 · Ctrl+드래그: 구간 비교
                </div>
                <div className="ranking-compare-top-chart">
                    {!showTopChart ? (
                      // 모든 상단 토글 OFF: 단지별 보기여도 차트 대신 안내문구 + 토글바 표시
                      <>
                        {renderTopPlaceholder('sale')}
                        {renderTopPlaceholder('lease')}
                      </>
                    ) : useSplitTopChart ? (
                      <SplitTopChart
                        {...commonChartProps}
                        showSaleTopChart={showSaleTopChart}
                        showLeaseTopChart={showLeaseTopChart}
                        registerSplitRef={registerSplitRef}
                        height={mainTopChartHeight}
                      />
                    ) : (
                      <>
                        {showSaleTopChart ? (
                          <TopChart {...commonChartProps} variant="sale" chartRef={topSaleChartRef} height={mainTopChartHeight} />
                        ) : renderTopPlaceholder('sale')}
                        {showSaleTopChart && showSaleCountChart && (
                          <CountChart {...commonChartProps} variant="sale" wrapperClassName="ranking-compare-chart-section ranking-compare-count-merged" panelPrefix="ranking-compare-combined-sale-count" chartRef={saleCountChartRef} height={countChartHeight} />
                        )}
                        {showLeaseTopChart ? (
                          <TopChart {...commonChartProps} variant="lease" chartRef={topLeaseChartRef} height={mainTopChartHeight} />
                        ) : renderTopPlaceholder('lease')}
                        {showLeaseTopChart && showLeaseCountChart && (
                          <CountChart {...commonChartProps} variant="lease" wrapperClassName="ranking-compare-chart-section ranking-compare-count-merged" panelPrefix="ranking-compare-combined-lease-count" chartRef={leaseCountChartRef} height={countChartHeight} />
                        )}
                      </>
                    )}
                  </div>

                {(showSaleCountChart || showLeaseCountChart) && (
                  <div className="ranking-compare-bottom-charts">
                    {showSaleCountChart && !showSaleTopChart && (
                      <CountChart {...commonChartProps} variant="sale" wrapperClassName="ranking-compare-sub-chart" panelPrefix="ranking-compare-bottom-sale-count" chartRef={saleCountChartRef} height={countChartHeight} />
                    )}
                    {showLeaseCountChart && !showLeaseTopChart && (
                      <CountChart {...commonChartProps} variant="lease" wrapperClassName="ranking-compare-sub-chart" panelPrefix="ranking-compare-bottom-lease-count" chartRef={leaseCountChartRef} height={countChartHeight} />
                    )}
                  </div>
                )}
              </div>
              </div>
            </div>
          )}
        </div>

        <RankerComplexSearchPopup
          open={searchPopupOpen}
          onClose={() => setSearchPopupOpen(false)}
          onSelect={handleAddCompareTarget}
          excludeCodes={compareTargets.map((target) => `${String(target?.code || '')}|${String(target?.ptpNo || '')}`)}
          title="비교 단지 추가"
        />
      </div>
    </div>
  );
}

export default RankingCompareModalV2;
